import { decamelizeKeys } from 'humps'
import { all, call, delay, fork, put, race, select, take, takeLatest } from 'redux-saga/effects'

import AppMonitoring from 'utilities/monitoring'
import * as actions from './actions'
import { APP_BECAME_VISIBLE } from 'state/rootActions'
import * as APIActions from '../api/actions'
import { passwordResetActions } from 'state/passwordReset'
import history from 'state/history'
import { setParams } from 'utilities/apiQueryString'
import { requestActions } from 'state/request'
import { failure, success } from 'utilities/actions'
import { refreshAtFromExpiry } from './helpers'

import { API_URL, USER_SESSION_ENDPOINT, CLIENT_SETTINGS_ENDPOINT, LANDING_PAGE_URL, LOGOUT_ENDPOINT } from 'qap/constants'

function* fetchSessionInfo(action: any): Generator<any, any, any> {
  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url: `${API_URL}/${USER_SESSION_ENDPOINT}`,
    successAction: success(actions.API_SESSION_FETCH),
    failureAction: failure(actions.API_SESSION_FETCH)
  })
}

// Simple in-memory queue of { key, value }
const queuedClientSettingsUpdates: Array<{
  key: string;
  value: unknown;
}> = []

function* expireSession(action: any): Generator<any, any, any> {
  yield call(clearStorages)
}

function* fetchApiSpecification(): Generator<any, any, any> {
  yield put(APIActions.fetchApiSpecification())
}

function* fetchSystemStatus(): Generator<any, any, any> {
  yield put(APIActions.fetchSystemStatus())
}

function* onAuthenticated(action: any): Generator<any, any, any> {
  yield fetchSessionInfo(action)
  yield fetchApiSpecification()
  yield fetchSystemStatus()

  // run any queued settings updates *as part of* the auth flow
  yield flushQueuedClientSettingsUpdates()
}

export function* clearStorages(): Generator<any, any, any> {
  yield put(actions.cleanStoreState())
  yield call(clearBrowserStorages)
}

export function clearBrowserStorages() {
  // Remove all local and session storage as we no longer store any non-essential data
  sessionStorage.clear()
  localStorage.clear()
}

export function* onSessionFetched(): Generator<any, any, any> {
  const userId = yield select((state) => state.auth?.user?.id)

  yield call(AppMonitoring.setUserId, userId)
}

function* onJwtAuthenticated(action: any): Generator<any, any, any> {
// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value.
  const { data: { secureId } = {}, redirect, notification } = action

  if (secureId) {
    history.replace(`/create-account/${secureId}${setParams({ redirect, notification })}`)
  } else {
    yield put({
      ...action,
      type: success(actions.API_SESSION_PASSWORD_AUTH_CREATE),
    })

    history.replace(redirect || LANDING_PAGE_URL)
  }
}

export function* handleLogoutSuccess(): Generator<any, any, any> {
  // Clean local state and local storage
  yield call(clearStorages)

  // Remove user ID from App monitoring
  yield call(AppMonitoring.setUserId, undefined)
}

export function* logoutUser(): Generator<any, any, any> {
  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url: `${API_URL}/${LOGOUT_ENDPOINT}`,
    successAction: success(actions.LOGOUT_USER),
    failureAction: failure(actions.LOGOUT_USER),
  })
}

function* watchAuthSuccess(): Generator<any, any, any> {
  yield takeLatest(
    [
      success(actions.API_SESSION_PASSWORD_AUTH_CREATE),
      success(passwordResetActions.API_PASSWORD_RESETS_RESETS_CREATE),
    ],
    onAuthenticated,
  )
  yield takeLatest([success(actions.API_SESSION_JWT_AUTH_CREATE)], onJwtAuthenticated)
  yield takeLatest([actions.AUTH_EXPIRE_SESSION], expireSession)
}

function* updateClientSettings(action: any): Generator<any, any, any> {
  const {
    payload: { key, value },
  } = action
  const body = { data: decamelizeKeys({ [key]: value }) } as const

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PATCH',
    url: `${API_URL}${CLIENT_SETTINGS_ENDPOINT}`,
    body,
    successAction: success(actions.API_SESSION_CLIENT_SETTINGS_UPDATE),
    failureAction: failure(actions.API_SESSION_CLIENT_SETTINGS_UPDATE),
  })
}

function queueClientSettingsUpdateWorker(action: any) {
  if (action?.payload) {
    const { key, value } = action.payload
    queuedClientSettingsUpdates.push({ key, value })
  }
}

function* queueClientSettingsUpdateSaga(action: any): Generator<any, any, any> {
  yield queueClientSettingsUpdateWorker(action)
}

function* flushQueuedClientSettingsUpdates(): Generator<any, any, any> {
  while (queuedClientSettingsUpdates.length > 0) {
// @ts-expect-error - TS2339 - Property 'key' does not exist on type '{ key: string; value: unknown; } | undefined'. | TS2339 - Property 'value' does not exist on type '{ key: string; value: unknown; } | undefined'.
    const { key, value } = queuedClientSettingsUpdates.shift()
    yield put(actions.updateClientSettings({ key, value }))
  }
}

function* refreshSession(): Generator<any, any, any> {
  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url: `${API_URL}/${USER_SESSION_ENDPOINT}/refresh`,
    successAction: success(actions.API_SESSION_REFRESH),
    failureAction: failure(actions.API_SESSION_REFRESH),
    skipGlobalErrorHandling: true,
  })
}

/**
 * Worker: Calculates wait time, sleeps, and triggers the session refresh action
 */
export function* scheduleSessionRefreshWorker(
  action: any,
  // Minimum wait time of 1 minute
  minWaitMs = 60_000,
): Generator<any, any, any> {
  const expiry = yield select((state) => state.auth?.expiry)

  if (!expiry) return

  const nowMs = Date.now()
  let nextRefreshAtMs = refreshAtFromExpiry(expiry, nowMs)

  // Token already expired?
  if (!nextRefreshAtMs) {
    yield put(actions.expireSession())
    return
  }

  // Schedule the wait
  let waitMs = Math.max(nextRefreshAtMs - nowMs, 0)

  // If the wait time is less than our minimum, just schedule the refresh immediately
  if (waitMs < minWaitMs) {
    // unless if we just failed a refresh, and the wait time is too low, expire now
    if (action.type === failure(actions.API_SESSION_REFRESH)) {
      yield put(actions.expireSession())
      return
    }
    waitMs = 0
    nextRefreshAtMs = nowMs
  }

  yield put(actions.scheduleSessionRefresh(nextRefreshAtMs, nowMs))

  // Race against cancellation events
  // Note: We do NOT need to race against `success(actions.API_SESSION_REFRESH)` here
  // because `takeLatest` in the watcher handles that implicit cancellation
  const { timeout } = yield race({
    timeout: delay(waitMs),
    logout: take(success(actions.LOGOUT_USER)),
    expire: take(actions.AUTH_EXPIRE_SESSION),
  })

  // If we didn't timeout, it means we logged out or expired manually. Stop
  if (!timeout) return

  // Trigger the refresh - This will trigger the API call
  // If it succeeds -> `success(API_SESSION_REFRESH)` fires -> Watcher restarts this worker
  yield put(actions.refreshSession())
}

/**
 * Watcher: Listens for any action that results in a valid session/expiry
 * and starts the scheduling worker
 */
function* watchSessionUpdatesForRefresh(): Generator<any, any, any> {
  yield takeLatest(
    [
      // Any time we get fresh session data (login, fetch, or refresh), schedule the NEXT refresh
      // We usually don't need `API_SESSION_PASSWORD_AUTH_CREATE` here because
      // `onAuthenticated` triggers `fetchSessionInfo`, which triggers `API_SESSION_FETCH`.
      success(actions.API_SESSION_FETCH),
      success(actions.API_SESSION_REFRESH),
      failure(actions.API_SESSION_REFRESH),
      APP_BECAME_VISIBLE,
    ],
    scheduleSessionRefreshWorker
  )
}

export default function* authSagas(): Generator<any, any, any> {
  yield all([
    fork(watchAuthSuccess),
    fork(watchSessionUpdatesForRefresh),
    takeLatest(actions.API_SESSION_FETCH, fetchSessionInfo),
    takeLatest(actions.API_SESSION_CLIENT_SETTINGS_UPDATE, updateClientSettings),
    takeLatest(success(actions.API_SESSION_FETCH), onSessionFetched),
    takeLatest(actions.LOGOUT_USER, logoutUser),
    takeLatest(success(actions.LOGOUT_USER), handleLogoutSuccess),
    takeLatest(actions.QUEUE_CLIENT_SETTINGS_UPDATE, queueClientSettingsUpdateSaga),
    takeLatest(actions.API_SESSION_REFRESH, refreshSession),
  ])
}
